/**
 * Context quản lý dialog đăng nhập/đăng ký toàn cục.
 *
 * Render LoginDialog và RegisterDialog ở root provider.
 * Hỗ trợ mở dialog qua location.state (openLogin, openRegister, returnTo).
 */
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginDialog } from '../components/Common/LoginDialog';
import { RegisterDialog } from '../components/Common/RegisterDialog';

/** Context value cho trạng thái và hàm mở/đóng dialog auth. */
export const AuthDialogContext = createContext(null);

/** Provider bọc app, mount dialog login/register. */
export const AuthDialogProvider = ({ children }) => {
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const loginSuccessRef = useRef(null);
  const registerSuccessRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const openLoginDialog = useCallback((options = {}) => {
    setIsRegisterOpen(false);
    loginSuccessRef.current = options.onSuccess || null;
    setIsLoginOpen(true);
  }, []);

  const closeLoginDialog = useCallback(() => {
    setIsLoginOpen(false);
    loginSuccessRef.current = null;
  }, []);

  const openRegisterDialog = useCallback((options = {}) => {
    setIsLoginOpen(false);
    registerSuccessRef.current = options.onSuccess || null;
    setIsRegisterOpen(true);
  }, []);

  const closeRegisterDialog = useCallback(() => {
    setIsRegisterOpen(false);
    registerSuccessRef.current = null;
  }, []);

  const handleLoginSuccess = useCallback(() => {
    const callback = loginSuccessRef.current;
    loginSuccessRef.current = null;
    callback?.();
  }, []);

  const handleRegisterSuccess = useCallback(() => {
    const callback = registerSuccessRef.current;
    registerSuccessRef.current = null;
    callback?.();
  }, []);

  useEffect(() => {
    if (!location.state?.openLogin && !location.state?.openRegister) {
      return;
    }

    const { returnTo } = location.state;
    const onSuccess = returnTo ? () => navigate(returnTo, { replace: true }) : undefined;

    if (location.state.openLogin) {
      openLoginDialog({ onSuccess });
    } else if (location.state.openRegister) {
      openRegisterDialog({ onSuccess });
    }

    navigate(`${location.pathname}${location.search}${location.hash}`, {
      replace: true,
      state: {},
    });
  }, [location, navigate, openLoginDialog, openRegisterDialog]);

  const value = {
    isLoginOpen,
    isRegisterOpen,
    openLoginDialog,
    closeLoginDialog,
    openRegisterDialog,
    closeRegisterDialog,
  };

  return (
    <AuthDialogContext.Provider value={value}>
      {children}
      <LoginDialog
        isOpen={isLoginOpen}
        onClose={closeLoginDialog}
        onSuccess={handleLoginSuccess}
        onSwitchToRegister={openRegisterDialog}
      />
      <RegisterDialog
        isOpen={isRegisterOpen}
        onClose={closeRegisterDialog}
        onSuccess={handleRegisterSuccess}
        onSwitchToLogin={openLoginDialog}
      />
    </AuthDialogContext.Provider>
  );
};
